/**
 * API Client for auth-service frontend
 */

import { isProxied } from '../proxy-config';
// When proxied, use same-origin (Caddy routes /api/* to the backend).
// Direct: use the env var or localhost fallback.
//
// 999.1551: REACT_APP_AUTH_API_URL is the standard CRA build-time env-var
// pattern (pre-approved). The fallback is dev-only; production builds inject
// the real URL at build time. Already gated through proxy-config's isProxied
// check — when proxied (localdev/production), same-origin is used and this
// env var is not read at all.
const API_BASE = isProxied ? '' : (process.env.REACT_APP_AUTH_API_URL || 'http://localhost:5010');

class ApiClient {
  constructor() {
    this.baseUrl = API_BASE;
  }

  getToken() {
    return localStorage.getItem('auth_access_token');
  }

  setTokens(accessToken, refreshToken) {
    localStorage.setItem('auth_access_token', accessToken);
    if (refreshToken) localStorage.setItem('auth_refresh_token', refreshToken);
  }

  clearTokens() {
    localStorage.removeItem('auth_access_token');
    localStorage.removeItem('auth_refresh_token');
    localStorage.removeItem('auth_user');
  }

  // `authToken` (999.18844 #5): a purpose-scoped bearer (the age_attest token
  // from a login door's 403) sent INSTEAD of the stored session token; such a
  // request never triggers a session refresh. Present-but-empty is refused.
  async request(path, options = {}) {
    const { authToken, ...fetchOptions } = options;
    const scoped = Object.prototype.hasOwnProperty.call(options, 'authToken');
    if (scoped && (typeof authToken !== 'string' || authToken === '')) {
      throw new Error('apiClient: authToken must be a non-empty string');
    }
    const url = `${this.baseUrl}${path}`;
    const headers = { 'Content-Type': 'application/json', ...fetchOptions.headers };

    const token = scoped ? authToken : this.getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(url, { ...fetchOptions, headers, credentials: 'include' });

    // Auto-refresh on 401 (session requests only)
    if (response.status === 401 && !scoped && !options._isRetry) {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        return this.request(path, { ...options, _isRetry: true });
      }
    }

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const error = new Error(data.error || data.message || 'Request failed');
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  }

  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('auth_refresh_token');
      if (!refreshToken) return false;

      const response = await fetch(`${this.baseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ refreshToken })
      });

      if (!response.ok) return false;

      const data = await response.json();
      this.setTokens(data.tokens.accessToken, data.tokens.refreshToken);
      return true;
    } catch {
      return false;
    }
  }

  get(path) { return this.request(path); }
  post(path, body, opts = {}) { return this.request(path, { ...opts, method: 'POST', body: JSON.stringify(body) }); }
  put(path, body) { return this.request(path, { method: 'PUT', body: JSON.stringify(body) }); }
  del(path) { return this.request(path, { method: 'DELETE' }); }

  // --- MCP API keys ---
  // Backend filters GET to the user's key_type='mcp' keys; create returns the
  // full `api_key` secret ONCE (only on this create response).
  listMcpKeys() { return this.get('/api/api-keys'); }
  createMcpKey(body) { return this.post('/api/api-keys', body); }
  revokeMcpKey(id) { return this.post(`/api/api-keys/${id}/revoke`); }
}

const apiClient = new ApiClient();
export default apiClient;
